import autobind from 'core-decorators/lib/autobind';
import classnames from 'classnames';
import getScrollParent from 'scrollparent';

import { WixConnect } from 'shared/utils/wix-connect';
import invertSide from 'shared/utils/popout/invert-side';
import { byTriangleSide } from 'shared/utils/popout/calculate-position';

import { sides, popoutPositions } from 'shared/constants/popout';

import dropdownOptionsShape from 'shared/shapes/dropdown-options';

import Icon from 'shared/components/icon/icon';
import Popout, { showPopout } from 'shared/components/popout/popout';
import {
  WidgetScrollbarWrapper,
  BackOfficeScrollbarWrapper,
} from 'shared/components/scrollbar-wrapper/scrollbar-wrapper';

import styles from './dropdown.styl';


@WixConnect(styles)
class Option extends React.Component {

  static propTypes = {
    selected: React.PropTypes.bool,
    label: React.PropTypes.string,
    value: React.PropTypes.any,
    onClick: React.PropTypes.func,
    className: React.PropTypes.string,
    height: React.PropTypes.number,
  };

  static defaultProps = {
    selected: false,
  };

  @autobind
  handleClick() {
    const { value, onClick } = this.props;
    onClick(value);
  }

  render() {
    const { selected, className, label, height } = this.props;

    const styleNames = classnames('option', {
      selected,
    });

    const heightStyle = { height };

    return (
      <div styleName={styleNames}
           className={className}
           data-hook="dropdown-option"
           title={label}
           onClick={this.handleClick}
           style={heightStyle}>
        {label}
      </div>
    );
  }
}


@WixConnect(styles)
class BaseDropdown extends React.Component {

  static propTypes = {
    value: React.PropTypes.any,
    options: dropdownOptionsShape,
    placeholder: React.PropTypes.string,
    onChange: React.PropTypes.func,
    onOpen: React.PropTypes.func,
    rounded: React.PropTypes.bool,
    className: React.PropTypes.string,
    dataHook: React.PropTypes.string,
    labelClassName: React.PropTypes.string,
    triangleClassName: React.PropTypes.string,
    optionsClassName: React.PropTypes.string,
    optionClassName: React.PropTypes.string,
    selectedOptionClassName: React.PropTypes.string,
    popoutTriangleSide: React.PropTypes.string,
    popoutPosition: React.PropTypes.string,
    height: React.PropTypes.number,
    disabled: React.PropTypes.bool,
    maxOptionsVisible: React.PropTypes.number,
    enablePopoutPositioningUpdate: React.PropTypes.bool,
    ScrollbarWrapper: React.PropTypes.oneOf([WidgetScrollbarWrapper, BackOfficeScrollbarWrapper]),
  };

  static defaultProps = {
    height: 32,
    maxOptionsVisible: 5,
    onChange: _.noop,
    onOpen: _.noop,
    rounded: false,
    disabled: false,
    popoutTriangleSide: sides.NONE,
    popoutPosition: popoutPositions.LEFT,
    enablePopoutPositioningUpdate: false,
    ScrollbarWrapper: BackOfficeScrollbarWrapper,
  };

  state = {
    open: false,
    hidePopout: true,
  };

  componentDidUpdate() {
    this.updatePopoutPosition();
  }

  calculatePosition() {
    const { popoutTriangleSide: triangleSide, popoutPosition } = this.props;
    return byTriangleSide(this.triangle, this.popout, { triangleSide, popoutPosition });
  }

  updatePopoutPosition() {
    if (!this.props.enablePopoutPositioningUpdate) {
      return;
    }

    const wrapperRect = this.wrapper.getBoundingClientRect();

    // getting scroll parent twice, becase first is not enough
    const { scrollLeft } = getScrollParent(getScrollParent(this.wrapper));

    let { left, top, offsetX } = this.calculatePosition();
    left -= scrollLeft + wrapperRect.left - offsetX;
    top -= wrapperRect.top;

    this.popout.style.left = `${left}px`;
    this.popout.style.top = `${top}px`;
  }

  @autobind
  handleClickOutside() {
    if (this.state.open) {
      this.setOpenState(false);
    }
  }

  @autobind
  handleLabelClick(event) {
    event.nativeEvent.stopImmediatePropagation();
    this.setOpenState(!this.state.open);
  }

  setOpenState(open) {
    if (open) {
      this.setState({
        open,
        hidePopout: false,
      });

      this.props.onOpen();
      showPopout(this.popout);
    } else {
      this.setState({
        open,
      });

      setTimeout(() => {
        this.setState({
          hidePopout: true,
        });
      }, 200);
    }
  }

  @autobind
  handleOptionClick(value) {
    const { onChange } = this.props;
    const didValueChange = value !== this.props.value;

    this.setOpenState(false);

    if (didValueChange) {
      onChange(value);
    }
  }

  @autobind
  getTriangleRef(node) {
    this.triangle = node;
  }

  @autobind
  getPopoutRef(node) {
    this.popout = node;
  }

  render() {
    const {
      value, rounded, disabled, popoutTriangleSide, popoutPosition,
      className, optionsClassName, optionClassName, selectedOptionClassName, labelClassName,
      triangleClassName, ScrollbarWrapper, height, maxOptionsVisible, placeholder,
      enablePopoutPositioningUpdate, dataHook,
    } = this.props;
    let { options } = this.props;
    const { open, hidePopout } = this.state;

    const styleNames = classnames('dropdown', {
      open,
      'hide-popout': hidePopout,
      rounded,
      disabled,
    });

    const selectedOption = _.find(options, option => _.isEqual(option.value, value));
    let label;

    if (selectedOption) {
      label = selectedOption.label;
    } else {
      label = (
        <span styleName="placeholder">
          {placeholder}
        </span>
      );
    }

    const heightStyle = { height };

    options = _.map(options, (option, i) => {
      if (option.categoryLabel) {
        return (
          <div key={`category-${option.categoryLabel}`}
               styleName="category">
            {option.categoryLabel}
          </div>
        );
      }

      const isSelected = _.isEqual(value, option.value);
      const classNames = classnames(optionClassName, {
        [selectedOptionClassName]: selectedOptionClassName && isSelected,
      });

      return (
        <Option key={i}
                label={option.label}
                value={option.value}
                selected={isSelected}
                className={classNames}
                height={height}
                onClick={this.handleOptionClick} />
      );
    });

    let popoutHeightStyle = 0;
    if (options.length > maxOptionsVisible) {
      options = (
        <ScrollbarWrapper>
          {options}
        </ScrollbarWrapper>
      );

      popoutHeightStyle = height * maxOptionsVisible;
    }

    const stickStyleNames = enablePopoutPositioningUpdate ? {} : {
        'stick-to-left': popoutPosition === popoutPositions.LEFT,
        'stick-to-center': popoutPosition === popoutPositions.CENTER,
        'stick-to-right': popoutPosition === popoutPositions.RIGHT,
        'stick-to-bottom': popoutPosition === popoutPositions.TOP,
      };

    const optionsStyleNames = classnames('options', {
      single: options.length === 1,
      ...stickStyleNames,
    });

    return (
      <div ref={node => (this.wrapper = node)}
           styleName={styleNames}
           className={className}
           data-hook={dataHook}
           style={heightStyle}>
        <div styleName="label"
             data-hook="dropdown-label"
             className={labelClassName}
             style={heightStyle}
             onClick={this.handleLabelClick}>
          <span styleName="value"
                title={label}>{label}</span>
          <Icon getRef={this.getTriangleRef}
                styleName="triangle-down"
                className={triangleClassName}
                name="triangle-down" />
        </div>

        <Popout getRef={this.getPopoutRef}
                styleName={optionsStyleNames}
                className={optionsClassName}
                innerClassName={styles['options-inner']}
                popoutSide={invertSide(popoutTriangleSide)}
                height={popoutHeightStyle}
                onClickOutside={this.handleClickOutside}>
          {options}
        </Popout>
      </div>
    );
  }
}


@WixConnect(styles)
export default class BackOfficeDropdown extends React.Component {
  render() {
    const props = _.omit(this.props, 'styles');

    return (
      <BaseDropdown {...props}
                    height={36}
                    styleName="backoffice"
                    ScrollbarWrapper={BackOfficeScrollbarWrapper} />
    );
  }
}


@WixConnect(styles)
export class WidgetDropdown extends React.Component {
  render() {
    const props = _.omit(this.props, 'styles');

    return (
      <BaseDropdown {...props}
                    styleName="widget"
                    ScrollbarWrapper={WidgetScrollbarWrapper} />
    );
  }
}


@WixConnect(styles)
export class StripWidgetDropdown extends React.Component {
  render() {
    const props = _.omit(this.props, 'styles');

    return (
      <BaseDropdown {...props}
                    styleName="widget strip"
                    ScrollbarWrapper={WidgetScrollbarWrapper} />
    );
  }
}


@WixConnect(styles)
export class BareDropdown extends React.Component {
  render() {
    const props = _.omit(this.props, 'styles');

    return (
      <BaseDropdown {...props}
                    styleName="bare"
                    enablePopoutPositioningUpdate
                    popoutPosition={popoutPositions.CENTER}
                    popoutTriangleSide={sides.TOP} />
    );
  }
}



// WEBPACK FOOTER //
// ./src/shared/components/dropdown/dropdown.js